import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, RefreshCw } from 'lucide-react';

const PaymentCallback = () => {
  const { API, user, language, switchRole, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const verificationAttempted = useRef(false);

  const t = {
    en: {
      processing: 'Processing Payment',
      verifying: 'Verifying your payment...',
      success: 'Payment Successful!',
      failed: 'Payment Failed',
      tryAgain: 'Try Again',
      backToDashboard: 'Back to Dashboard',
      redirecting: 'Redirecting...',
      subscriptionSuccess: 'Subscription activated successfully!',
      expires: 'Expires',
      paymentCancelled: 'Payment was cancelled or failed. Please try again.',
      missingToken: 'Payment verification failed. Missing payment reference.',
      contactSupport: 'Payment verification failed. Please contact support if you were charged.',
      retrying: 'Retrying verification...'
    },
    de: {
      processing: 'Zahlung wird verarbeitet',
      verifying: 'Ihre Zahlung wird überprüft...',
      success: 'Zahlung erfolgreich!',
      failed: 'Zahlung fehlgeschlagen',
      tryAgain: 'Erneut versuchen',
      backToDashboard: 'Zurück zum Dashboard',
      redirecting: 'Weiterleitung...',
      subscriptionSuccess: 'Abonnement erfolgreich aktiviert!',
      expires: 'Gültig bis',
      paymentCancelled: 'Zahlung wurde storniert oder ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
      missingToken: 'Zahlungsüberprüfung fehlgeschlagen. Fehlende Zahlungsreferenz.',
      contactSupport: 'Zahlungsüberprüfung fehlgeschlagen. Bitte kontaktieren Sie den Support, falls Ihnen Gebühren berechnet wurden.',
      retrying: 'Überprüfung wird wiederholt...'
    }
  };

  const text = t[language] || t.en;

  useEffect(() => {
    // Only run once
    if (verificationAttempted.current) return;
    verificationAttempted.current = true;

    const verifyPayment = async () => {
      const paymentStatus = searchParams.get('status');
      const plan = searchParams.get('plan');
      const type = searchParams.get('type') || (window.location.pathname.includes('verification') ? 'verification' : 'subscription');

      // Check for failed/cancelled status first
      if (paymentStatus === 'failed' || paymentStatus === 'cancelled') {
        setStatus('failed');
        setMessage(text.paymentCancelled);
        // Clean up stored token
        localStorage.removeItem('saferpay_token');
        return;
      }

      // Get token - try multiple sources
      let token = searchParams.get('token');
      if (!token) {
        token = localStorage.getItem('saferpay_token');
      }
      if (!token) {
        // Try to extract from URL hash or other formats
        const urlMatch = window.location.href.match(/token=([^&]+)/);
        if (urlMatch) {
          token = urlMatch[1];
        }
      }

      if (!token) {
        setStatus('failed');
        setMessage(text.missingToken);
        return;
      }

      console.log('Verifying payment with token:', token.substring(0, 10) + '...');
      setMessage(text.verifying);

      try {
        // Verify the payment with backend
        const response = await axios.post(`${API}/saferpay/verify?token=${encodeURIComponent(token)}`);

        if (response.data.success) {
          // Clean up stored token on success
          localStorage.removeItem('saferpay_token');
          setStatus('success');

          // Handle add_role payment type
          if (response.data.type?.startsWith('add_role_subscription_')) {
            const newRole = response.data.new_role;
            setMessage(`Your ${newRole} profile is now active!`);
            // Refresh user to get updated roles
            if (refreshUser) await refreshUser();
            // Switch active role to the new one
            if (switchRole && newRole) switchRole(newRole);
            setTimeout(() => {
              navigate(newRole === 'client' ? '/dashboard/client' : '/dashboard/professional');
            }, 2000);
          } else if (response.data.type?.startsWith('subscription_')) {
            const expiresDate = response.data.expires ? new Date(response.data.expires).toLocaleDateString() : '';
            setMessage(`${text.subscriptionSuccess} ${expiresDate ? `${text.expires}: ${expiresDate}` : ''}`);
            
            // Update user context
            if (refreshUser) await refreshUser();
            
            // Redirect to dashboard after 2.5 seconds
            setTimeout(() => {
              const dashboardPath = user?.role === 'client' ? '/dashboard/client' : '/dashboard/professional';
              navigate(dashboardPath);
            }, 2500);
          }
        } else {
          setStatus('failed');
          setMessage(response.data.error || text.contactSupport);
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        
        // If it's a 404 (payment not found), try again with delay
        if (error.response?.status === 404 && retryCount < 2) {
          setRetryCount(prev => prev + 1);
          setMessage(text.retrying);
          setTimeout(() => {
            verificationAttempted.current = false;
            verifyPayment();
          }, 2000);
          return;
        }
        
        setStatus('failed');
        const errorDetail = error.response?.data?.detail;
        if (typeof errorDetail === 'object') {
          setMessage(errorDetail.message || text.contactSupport);
        } else {
          setMessage(errorDetail || text.contactSupport);
        }
      }
    };

    verifyPayment();
  }, [searchParams, API, navigate, user, text, retryCount, refreshUser, switchRole]);

  const handleRetry = () => {
    const previousPage = localStorage.getItem('saferpay_return_page') || '/subscription-plans';
    navigate(previousPage);
  };

  const handleBackToDashboard = () => {
    const dashboardPath = user?.role === 'client' ? '/dashboard/client' : '/dashboard/professional';
    navigate(dashboardPath);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center py-12 px-4" data-testid="payment-callback-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="w-20 h-20 text-blue-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{text.processing}</h1>
              <p className="text-gray-600" data-testid="payment-processing-message">{message}</p>
              {retryCount > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  {language === 'en' ? `Attempt ${retryCount + 1}/3` : language === 'it' ? `Tentativo ${retryCount + 1}/3` : `Versuch ${retryCount + 1}/3`}
                </p>
              )}
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{text.success}</h1>
              <p className="text-gray-600 mb-6" data-testid="payment-success-message">{message}</p>
              <p className="text-sm text-gray-500">{text.redirecting}</p>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircle className="w-20 h-20 text-red-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{text.failed}</h1>
              <p className="text-gray-600 mb-6" data-testid="payment-failure-message">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  data-testid="payment-retry-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  {text.tryAgain}
                </button>
                <button
                  onClick={handleBackToDashboard}
                  data-testid="payment-back-dashboard-button"
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg"
                >
                  {text.backToDashboard}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallback;
